import type { EventRateLimiter } from '@datadog/browser-core'
import { isEmptyObject, display, createEventRateLimiter, buildTags } from '@datadog/browser-core'
import { DISCARDED } from '@datadog/js-core/assembly'
import { combine } from '@datadog/js-core/util'
import type { RumEventDomainContext } from '../domainContext.types'
import type { AssembledRumEvent } from '../rawRumEvent.types'
import { RumEventType, VitalType } from '../rawRumEvent.types'
import type { LifeCycle } from './lifeCycle'
import { LifeCycleEventType } from './lifeCycle'
import type { RumConfiguration } from './configuration'
import type { ModifiableFieldPaths } from './limitModification'
import { limitModification } from './limitModification'
import type { AssembleHookParams, Hooks } from './hooks'

const COMMON_MODIFIABLE_FIELD_PATHS: ModifiableFieldPaths = {
  'view.name': 'string',
  'view.url': 'string',
  'view.referrer': 'string',
  context: 'object',
  service: 'string',
  version: 'string',
}

const MODIFIABLE_FIELD_PATHS_BY_EVENT: Record<AssembledRumEvent['type'], ModifiableFieldPaths> = {
  [RumEventType.VIEW]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'view.performance.lcp.resource_url': 'string',
  },
  [RumEventType.ERROR]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'error.message': 'string',
    'error.stack': 'string',
    'error.handling_stack': 'string',
    'error.resource.url': 'string',
    'error.fingerprint': 'string',
    '_dd.debug_ids': 'array',
  },
  [RumEventType.RESOURCE]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'resource.url': 'string',
    'resource.graphql.variables': 'string',
    'resource.request.headers': 'object',
    'resource.response.headers': 'object',
  },
  [RumEventType.ACTION]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'action.target.name': 'string',
  },
  [RumEventType.LONG_TASK]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'long_task.scripts[].source_url': 'string',
    'long_task.scripts[].invoker': 'string',
    '_dd.debug_ids': 'array',
  },
  [RumEventType.VITAL]: {
    ...COMMON_MODIFIABLE_FIELD_PATHS,
    'vital.websocket.url': 'string',
    'vital.websocket.requested_protocols': 'array',
    'vital.websocket.selected_protocol': 'string',
    'vital.websocket.close_reason': 'string',
  },
}

export function startRumAssembly(
  configuration: RumConfiguration,
  lifeCycle: LifeCycle,
  hooks: Hooks,
  reportError: (message: string) => void,
  eventRateLimit?: number
) {
  const eventRateLimiters = {
    [RumEventType.ERROR]: createEventRateLimiter(RumEventType.ERROR, reportError, eventRateLimit),
    [RumEventType.ACTION]: createEventRateLimiter(RumEventType.ACTION, reportError, eventRateLimit),
    [RumEventType.VITAL]: createEventRateLimiter(RumEventType.VITAL, reportError, eventRateLimit),
  }

  lifeCycle.subscribe(
    LifeCycleEventType.RAW_RUM_EVENT_COLLECTED,
    ({ startClocks, duration, rawRumEvent, domainContext }) => {
      const assemblyParams = {
        eventType: rawRumEvent.type,
        rawRumEvent,
        domainContext,
        startTime: startClocks.relative,
        duration,
      } as AssembleHookParams
      const defaultRumEventAttributes = hooks.assembleEventDefaults.trigger(assemblyParams)!
      if (defaultRumEventAttributes === DISCARDED) {
        return
      }

      const rumEventAttributes = hooks.assembleEvent.trigger(assemblyParams)!
      if (rumEventAttributes === DISCARDED) {
        return
      }

      const serverRumEvent = combine(defaultRumEventAttributes, rawRumEvent, rumEventAttributes, {
        ddtags: buildTags(configuration).join(','),
      }) as AssembledRumEvent

      if (shouldSend(serverRumEvent, configuration.beforeSend, domainContext, eventRateLimiters)) {
        if (isEmptyObject(serverRumEvent.context!)) {
          delete serverRumEvent.context
        }
        lifeCycle.notify(LifeCycleEventType.RUM_EVENT_COLLECTED, serverRumEvent)
      }
    }
  )
}

function shouldSend(
  event: AssembledRumEvent,
  beforeSend: RumConfiguration['beforeSend'],
  domainContext: RumEventDomainContext,
  eventRateLimiters: { [key in RumEventType]?: EventRateLimiter }
) {
  if (beforeSend) {
    const result = limitModification(event, MODIFIABLE_FIELD_PATHS_BY_EVENT[event.type], (event) =>
      beforeSend(event, domainContext)
    )
    if (result === false) {
      const nonDismissibleEventLabel =
        event.type === RumEventType.VIEW
          ? 'view'
          : event.type === RumEventType.VITAL && event.vital?.type === VitalType.WEBSOCKET
            ? 'WebSocket vital'
            : undefined

      if (!nonDismissibleEventLabel) {
        return false
      }
      display.warn(`Can't dismiss ${nonDismissibleEventLabel} events using beforeSend!`)
    }
  }

  const rateLimitReached = eventRateLimiters[event.type]?.isLimitReached()

  return !rateLimitReached
}
